// Frontend input sanitization utilities
import DOMPurify from "dompurify";

// Sanitize HTML content
export const sanitizeHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["b", "i", "em", "strong", "p", "br"],
    ALLOWED_ATTR: [],
  });
};

// Sanitize text input
export const sanitizeText = (text) => {
  if (typeof text !== "string") return text;

  return text
    .trim()
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "") // Remove control characters
    .replace(/\s+/g, " "); // Normalize whitespace
};

// Sanitize email
export const sanitizeEmail = (email) => {
  if (typeof email !== "string") return "";

  return email
    .trim()
    .toLowerCase()
    .replace(/[^\w@.-]/g, ""); // Keep only alphanumeric, @, ., and -
};

// Sanitize phone number
export const sanitizePhone = (phone) => {
  if (typeof phone !== "string") return "";

  return phone.replace(/[^\d+]/g, ""); // Keep only digits and +
};

// Sanitize URL
export const sanitizeURL = (url) => {
  if (typeof url !== "string") return "";

  // Remove protocol if present and add https://
  let cleanUrl = url.trim();
  if (
    cleanUrl &&
    !cleanUrl.startsWith("http://") &&
    !cleanUrl.startsWith("https://")
  ) {
    cleanUrl = "https://" + cleanUrl;
  }

  return cleanUrl;
};

// Sanitize form data
export const sanitizeFormData = (formData) => {
  const sanitized = {};

  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === "string") {
      sanitized[key] = sanitizeText(value);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map((item) =>
        typeof item === "string" ? sanitizeText(item) : item
      );
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeFormData(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

// Validate and sanitize business data
export const sanitizeBusinessData = (business) => {
  return {
    ...business,
    businessName: sanitizeText(business.businessName || ""),
    businessDescription: sanitizeHTML(business.businessDescription || ""),
    productsServices: sanitizeHTML(business.productsServices || ""),
    businessWebsite: business.businessWebsite
      ? sanitizeURL(business.businessWebsite)
      : "",
    linkedinProfile: business.linkedinProfile
      ? sanitizeURL(business.linkedinProfile)
      : "",
    countriesOfOperation: (business.countriesOfOperation || []).map((country) =>
      sanitizeText(country)
    ),
  };
};

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate URL format
export const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Validate phone format
export const isValidPhone = (phone) => {
  const phoneRegex = /^\+?[\d\s\-()]{7,15}$/;
  return phoneRegex.test(phone);
};

// Sanitize search query
export const sanitizeSearchQuery = (query) => {
  return sanitizeText(query).substring(0, 100); // Limit to 100 characters
};























